import { useState, useEffect } from 'react'

import { subWeeks, format } from 'date-fns'
import Cookies from 'js-cookie'

import { useConfig } from '@client/contexts/ConfigProvider'
import { OrderDirectionEnum, OrderOrderEnum, OrderOriginEnum, OrderStatusEnum, OrderTypeEnum } from '@uctypes/api/globalTypes'

import { OrderItemFragment, OrdersQuery, useOrdersQuery } from './api'

interface ReturnType {
  mealKitRatingModalOpen: boolean
  mealKitRatingOrder: OrdersQuery['orders']['list'][0] | null
  loading: boolean
}

function isMealKitOrderItem(orderItem: OrderItemFragment): boolean {
  return orderItem?.product?.__typename === 'MealKit' && orderItem?.product?.name !== 'Fixed Subscription Fee'
}

function allMealKitsRated(orderItems: OrderItemFragment[]): boolean {
  for (const item of orderItems) {
    if (!item.orderItemRating) {
      return false
    }
  }
  return true
}

function getLast28DaysRange(currentDate: Date) {
  const now = currentDate || new Date()
  return {
    fromDate: format(subWeeks(now, 4), 'yyyy-MM-dd'),
    toDate: format(now, 'yyyy-MM-dd'),
  }
}

export const useMealKitRatingsLogic = (userId: string, isRegistered: boolean): ReturnType => {
  const config = useConfig()
  const [isClient, setIsClient] = useState(false)

  // Ensure this logic only runs on the client
  useEffect(() => {
    setIsClient(true)
  }, [])

  // On SSR, assume ratings are not dismissed by default (false), only check cookie on client
  const isRatingsDismissed = isClient ? Cookies.get('isDismissed') : false

  // Only fetch if user is registered, not dismissed, has a userId, and we are on the client
  const shouldSkip = !userId || !!isRatingsDismissed || !isRegistered || !isClient

  const { data: ordersData, loading: queryLoading } = useOrdersQuery({
    skip: shouldSkip,
    ssr: config.fetchSSRQuery(),
    variables: {
      filters: {
        users: [userId],
        status: [OrderStatusEnum.COMPLETE, OrderStatusEnum.PROCESSING],
        origin: [OrderOriginEnum.NODE],
        orderTypes: [OrderTypeEnum.SUBSCRIPTION],
        dispatchDateRange: getLast28DaysRange(new Date()),
      },
      order: {
        field: OrderOrderEnum.DISPATCH_DATE,
        direction: OrderDirectionEnum.DESC,
      },
      skip: 0,
      limit: 5,
    },
  })

  const isLoading = queryLoading || !isClient

  if (isLoading) {
    return {
      mealKitRatingModalOpen: false,
      mealKitRatingOrder: null,
      loading: true,
    }
  }

  if (ordersData?.orders?.list?.length) {
    const now = Date.now()
    // Find the most recent eligible order in the last 28 days
    const eligibleOrder = ordersData.orders.list.find(order => {
      // Use only the date part for dispatchDate
      const dispatchDate = new Date(order.dispatchDate.slice(0, 10)).getTime()
      const dayDiff = (now - dispatchDate) / (1000 * 60 * 60 * 24)
      const isSub = order.orderType === 'SUBSCRIPTION'
      const amountPaid = order.amountPaid
      const mealKitItems = (order.orderItems || []).filter(isMealKitOrderItem)
      const hasUnratedMealKit = mealKitItems.length > 0 && !allMealKitsRated(mealKitItems)

      // Robust eligibility check
      return (
        isSub &&
        !isRatingsDismissed &&
        dayDiff > 4 &&
        dayDiff < 28 &&
        amountPaid !== 0 &&
        hasUnratedMealKit
      )
    })

    if (eligibleOrder) {
      return {
        mealKitRatingModalOpen: true,
        mealKitRatingOrder: eligibleOrder,
        loading: false,
      }
    }
  }

  return {
    mealKitRatingModalOpen: false,
    mealKitRatingOrder: null,
    loading: false,
  }
}
